import * as Actions from '../actions/referralActions';
import Referral from '../../entities/referral';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ReferralAction = { 
    type: string; 
    referral: Referral | null; 
    referralId: string | Referral; 
    partial: boolean; 
    lastFetch: number; 
    referrals: Referral[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Referral> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Referral[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Referral> | null, firmId: string, userId: string, updates?: Referral[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'referral', 'referrals', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Referral>} data
 * @param {Referral[]} [updates]
 * @returns {ArrayMap<Referral>}
 */
function updateStorage(data: ArrayMap<Referral>, updates?: Referral[]): ArrayMap<Referral> {
	if (logging) console.debug('⮂ referralReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Referral[]} [updates]
 * @returns {ArrayMap<Referral>}
 */
function updateStorageNoReturn(updates?: Referral[]): void {
	if (logging) console.debug('⮂ referralReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Referral[]} [data]
 * @returns {ArrayMap<Referral>}
 */
function createState(data?: Referral[] | ArrayMap<Referral>): ArrayMap<Referral> {
	if (!data) return ArrayMapRegistry.clone<Referral>('referral', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Referral>('referral', data.map(e => e));
    else return ArrayMapRegistry.clone<Referral>('referral', data);
}

function referrals(state: ArrayMap<Referral>, action: ReferralAction): ArrayMap<Referral> {
	if (!state) state = createState();

	let st: ArrayMap<Referral>;

	switch (action.type) {
		// action.referral - the new value of referral with id action.id
		// action.referralId - id of the target, used if new value is null
		case Actions.REFERRAL_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ referralReducer: FETCH_ONE_SUCCESS', action);

			if (action.referral === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.referralId)) {
				st = state.clone();
				st.set(action.referral);
				// state.clear();

				updateStorageNoReturn([action.referral]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.referrals
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.referrals
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.referrals
			 !partial, !stateEmpty, fetchEmpty	-> D return referrals in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge referrals in state newer than those in action.referrals
		 */
		case Actions.REFERRAL_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.referrals.length === 0;
			const fetched = action.referrals.map(referral => new Referral({...referral, persisted: true}));

			if (logging) console.debug(`⮂ referralReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} referrals with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ referralReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ referralReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Referral> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Referral[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(referral => {
					const current = st.get(referral.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && referral.status === 'D') {
							// Remove it from state
							toUpdate.push(referral);
							st.delete(referral);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (referral.addedOn > current.addedOn) {
								toUpdate.push(referral);
								st.set(referral);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (referral.status !== 'D') {
							toUpdate.push(referral);
							st.set(referral);
						}
						else if (logging) console.debug('⮂ referralReducer: partialMerge: skipping deleted referral not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ referralReducer: empty: referrals', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ referralReducer: fullMerge: referrals', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localReferral => {
                    // If this referral has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localReferral.persisted) {
                        const fetchedReferral = st.get(localReferral.id);
                        if (!fetchedReferral || localReferral.addedOn > fetchedReferral.addedOn) st.set(localReferral);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.REFERRAL_FETCH_ONE_FAILURE:
			console.error('REFERRAL_FETCH_ONE_FAILURE', action.referralId, action.err);

			// No update to storage required
			return state;

		case Actions.REFERRAL_FETCH_FAILURE:
			console.error('REFERRAL_FETCH_FAILURE', action.referralId, action.err);

			// No update to storage required
			return state;

		case Actions.REFERRAL_RESTORE: {
		    let referral = new Referral({...action.referral});
            st = state.clone();

            const existing = st.get(referral.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (referral.addedOn >= existing.addedOn) {
                    st.set(referral);
                    return updateStorage(st, [referral]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (referral.status === 'D') return state;

                st.set(referral);
                return updateStorage(st, [referral]);
            }
        }
            
		case Actions.REFERRAL_SAVE: {
		    // console.debug('⮂ REFERRAL_SAVE reducer', action.referral?.id);
		    
			// clone the referral
			let referral = new Referral({...action.referral});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new referral in the state
			// If this is a create, add to the queue
			const _referral = st.get(referral.id);

			if (!_referral) st.set(referral);
			else {
				if (referral.addedOn >= _referral.addedOn) st.set(referral);
			}

			return updateStorage(st, [referral]);
		}

		case Actions.REFERRAL_SAVE_MANY: {
		    if (logging) console.debug('⮂ REFERRAL_SAVE_MANY reducer', action.referrals.map(referral => referral.id).join(','));
		    
			// clone the referrals
			let toProcess = action.referrals.map(referral => new Referral(referral));

			st = state.clone();
			// state.clear();

			const updates: Referral[] = [];

			toProcess.forEach(referral => {
				// Add or update the new referral in the state
				// If this is a create, add to the queue
				const existing = st.get(referral.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (referral.status !== 'D') {
						st.push(referral);
						updates.push(referral);
					}
					else if (logging) console.debug('⮂ REFERRAL_SAVE_MANY: skipping deleted referral not in state');
				}
				else {
					if (referral.addedOn >= existing.addedOn) {
						st.set(referral);
						updates.push(referral);
					}
					else if (logging) console.debug('⮂ Skipping, existing referral had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REFERRAL_REMOVE: {
			// clone the referral
			const referral = new Referral({...action.referral});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(referral);

			return updateStorage(st, [referral]);
		}

		case Actions.REFERRAL_RESTORE_FAILURE:
		case Actions.REFERRAL_SAVE_FAILURE: {
		    console.warn('REFERRAL_SAVE_FAILURE reducer', action.referral?.id);
		    if (!action.referral?.id) {
				console.warn('REFERRAL_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const referral = ensure(Referral, action.referral);
			if (!referral) {
				console.error('Could not find referral after call to save');
				return state;
			}

			st = state.clone();

			const _referralRef = state.get(referral.id);

			if (_referralRef && (_referralRef.addedOn === referral.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _referral = new Referral({..._referralRef});
				_referral.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_referral.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('referral', _referral).then(r => {
						if (r.isErr()) console.error(`⮂ referralReducer: failed to save referral with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ referralReducer: saved referral with fatalError info');
					}).catch(err => console.error(`⮂ referralReducer: failed to save referral with fatalError info: ${err.message}`));

				st.set(_referral);
			}

			return updateStorage(st);
		}

		case Actions.REFERRAL_SAVE_MANY_FAILURE: {
		    console.warn('REFERRAL_SAVE_MANY_FAILURE reducer', action.referrals.map(referral => referral.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.REFERRAL_RESTORE_SUCCESS:
		case Actions.REFERRAL_SAVE_SUCCESS: {
			if (!action.referral?.id) {
				console.warn('REFERRAL_SAVE_SUCCESS reducer');
                return state;
			}

			const referralRef = state.get(action.referral?.id);
			if (!referralRef) {
				console.warn('Could not find referral after call to save');
                return state;
			}

			st = state.clone();

			if (action.referral.addedOn >= referralRef.addedOn) {
				st.set(action.referral);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const referral = new Referral({...referralRef, persisted: true});
				st.set(referral);
			}

			return updateStorage(st, [action.referral]);
		}

		case Actions.REFERRAL_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ REFERRAL_SAVE_MANY_SUCCESS reducer', action.referrals.map(referral => referral.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const referrals: Referral[] = [];

			action.referrals.forEach(e => {
				const referralRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!referralRef) console.warn('Could not find referral after call to save');
				else if (e.addedOn >= referralRef.addedOn) {
				    st.set(e);
				    referrals.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const referral = new Referral({...referralRef, persisted: true});
					st.set(referral);
					referrals.push(referral);
				}
			});

			return updateStorage(st, referrals);
		}

		case Actions.REFERRAL_REMOVE_FAILURE: {
		    console.warn('REFERRAL_REMOVE_FAILURE action', action.referral?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.REFERRAL_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.REFERRAL_RESTORE_MANY: {
			if (logging) console.debug('⮂ REFERRAL_RESTORE_MANY reducer', action.referrals.map(referral => referral.id).join(','));

			const toProcess = action.referrals.map(referral => new Referral({...referral}));
			st = state.clone();

			const updates: Referral[] = [];

			toProcess.forEach(referral => {
				const existing = st.get(referral.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (referral.addedOn >= existing.addedOn) {
						st.set(referral);
						updates.push(referral);
					}
					else if (logging) console.debug('⮂ REFERRAL_RESTORE_MANY: skipping older version of referral');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (referral.status !== 'D') {
						st.set(referral);
						updates.push(referral);
					}
					else if (logging) console.debug('⮂ REFERRAL_RESTORE_MANY: skipping deleted referral not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.REFERRAL_REMOVE_MANY: {
			if (logging) console.debug('⮂ REFERRAL_REMOVE_MANY reducer', action.referrals.map(referral => referral.id).join(','));

			const toProcess = action.referrals.map(referral => new Referral({...referral}));
			st = state.clone();

			toProcess.forEach(referral => {
				st.delete(referral);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default referrals;